import confetti from "canvas-confetti";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Button } from "@/atoms-react/button/Button.react";
import DotsNavigation from "@/atoms-react/dots-navigation/DotsNavigation.react";
import Flag from "@/atoms-react/flag/Flag.react";
import Image from "@/atoms-react/image/Image.react";
import InputLabel from "@/atoms-react/input/Input.react.tsx";
import { Modal } from "@/atoms-react/modal/index.react";
import { RadioCard } from "@/atoms-react/radio-card/index.react";
import { Base, CardTitle, Info, SectionTitleSmall } from "@/atoms-react/text/Text.react";
import { LanguageSelector } from "@/components/lang-select/index.react.tsx";
import { useKYCContext } from "@/features/auth/hooks/use-kyc-context";
import { KYCDocumentType, KYCStatusType } from "@/features/kyc/types";
import { useAuthorization } from "@/features/user/authorization/hooks/use-check-authorisation.ts";
import { useUser } from "@/features/user/hooks/use-user";
import { useQuiz } from "@/features/user/quiz/hooks/use-quiz.ts";
import {
  ModalIbanAccountStep,
  type QuizResponses,
  type stepsSettingsType
} from "@/features/user/quiz/types";
import type { UserType } from "@/features/user/types";
import { useLocale } from "@/features/utils/hooks/use-locale";
import { HeaderIbanModal } from "@/molecules/modal-iban-account/header.react.tsx";
import { AccountReasonStep } from "@/molecules/modal-iban-account/quiz/account-reason-step.react.tsx";
import { AccountUsageStep } from "@/molecules/modal-iban-account/quiz/account-usage-step.react.tsx";
import { CheckCommercialRegisterStep } from "@/molecules/modal-iban-account/quiz/check-commercial-register-step.react.tsx";
import { CommercialRegisterInfoStep } from "@/molecules/modal-iban-account/quiz/commercial-register-info-step.react.tsx";
import { EuropeanAccountStep } from "@/molecules/modal-iban-account/quiz/european-account-step.react.tsx";
import { MonthlyIncomeStep } from "@/molecules/modal-iban-account/quiz/monthly-income-step.react.tsx";
import { NatureProActivityStep } from "@/molecules/modal-iban-account/quiz/nature-pro-activity-step.react.tsx";
import { PaymentUsageStep } from "@/molecules/modal-iban-account/quiz/payment-usage-step.react.tsx";
import { SourceIncomeStep } from "@/molecules/modal-iban-account/quiz/source-income-step.react.tsx";
import { VideoVerificationStep } from "@/molecules/modal-iban-account/quiz/video-verification-step.react.tsx";
import { Themes } from "@/types/theme";
import { NifVerificationStep } from "../modal-iban-account/quiz/nif-verification-step.react";
import { DocumentTypeStep } from "../kyc-identity/components/document-type-step.react";
import { WelcomeStep } from "../kyc-identity/components/welcome-step.react";
import { Logo } from "@/components/logo/Logo.react";

type ModalIbanAccountProps = {
  isOpen: boolean;
  onClose: () => void;
  allowedConsoPartner?: boolean;
  bannerCreateAccountUrl?: string;
} & React.ComponentPropsWithoutRef<"div">;

export const ModalIbanUniqAccount = ({
  isOpen,
  onClose,
  bannerCreateAccountUrl
}: ModalIbanAccountProps) => {
  const { t } = useTranslation(["pass"]);
  const { status } = useKYCContext();
  const [step, setStep] = useState<ModalIbanAccountStep>(ModalIbanAccountStep.WelcomeStep);
  const { useUserProfile } = useUser();
  const { data: user, refetch: refetchUser } = useUserProfile();
  const [progress, setProgress] = useState(0);
  const [nifVerificationStep, setNifVerificationStep] = useState<number>(6);
  const [totalSteps, setTotalSteps] = useState<number>(12);

  const [typeofProAccount, setTypeofProAccount] = useState<boolean>(false);
  const [inscritRegisterCommerce, setInscritRegisterCommerce] = useState<boolean>(false);
  const { useCountries } = useUser();
  const { locale } = useLocale();
  const { data: countries } = useCountries(locale.language);
  const [loadingAuthorized, setLoadingAuthorized] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 10;
  const { useIsValidatedIbanQuizMutation } = useQuiz();
  const { mutateAsync: isValidatedIban } = useIsValidatedIbanQuizMutation();
  const { useCheckAuthorizationMutation } = useAuthorization();
  const { mutateAsync: saveRequestAccount } = useCheckAuthorizationMutation();
  const { useCreateQuizMutation } = useQuiz();
  const { mutateAsync: saveQuizResponses } = useCreateQuizMutation();
  const nothasIban = user?.naturalPerson?.codeShop === null;
  const interval = 100;
  const totalDuration = 3000;
  const [ibanVerifiedQuiz, setIbanVerifiedQuiz] = useState<string>();
  const elapsed = useRef<number>(0);
  const timer = useRef<NodeJS.Timeout>(null);
  const skipNif = (() => {
    const find = user?.naturalPerson?.TaxAddress?.find((item) => {
      return item.tin === null || (item.tin && item.tin.length === 0) || item.tin === "";
    });
    return !find ? true : false;
  })();
  const verifiedIban = (() => {
    if (user?.naturalPerson.AliasIBAN && user?.naturalPerson.AliasIBAN.length > 0) {
      return user?.naturalPerson.AliasIBAN.filter((iban) => iban.valid);
    }
    return [];
  })();

  const stepsSettings: stepsSettingsType = {
    IdentityDocument: {
      current: 1,
      previous: ModalIbanAccountStep.WelcomeStep,
      next: ModalIbanAccountStep.Start
    },
    Start: {
      current: 2,
      previous: ModalIbanAccountStep.IdentityDocument,
      next: ModalIbanAccountStep.SourcesIncome
    },
    SourcesIncome: {
      current: 3,
      previous: ModalIbanAccountStep.Start,
      next: ModalIbanAccountStep.MonthlyIncome
    },
    MonthlyIncome: {
      current: 4,
      previous: ModalIbanAccountStep.SourcesIncome,
      next: ModalIbanAccountStep.AccountReasons
    },
    AccountReasons: {
      current: 5,
      previous: ModalIbanAccountStep.MonthlyIncome,
      next: ModalIbanAccountStep.PaymentUsage
    },
    PaymentUsage: {
      current: 6,
      previous: ModalIbanAccountStep.AccountReasons,
      next: ModalIbanAccountStep.AccountUsage
    },
    AccountUsage: {
      current: 7,
      previous: ModalIbanAccountStep.PaymentUsage,
      next: typeofProAccount
        ? ModalIbanAccountStep.NatureProActivity
        : skipNif
          ? ModalIbanAccountStep.EuropeanAccount
          : ModalIbanAccountStep.NifVerification
    },
    NatureProActivity: {
      current: 8,
      previous: ModalIbanAccountStep.AccountUsage,
      next: ModalIbanAccountStep.CheckCommercialRegister
    },
    CheckCommercialRegister: {
      current: 9,
      previous: ModalIbanAccountStep.NatureProActivity,
      next: inscritRegisterCommerce
        ? ModalIbanAccountStep.CommercialRegisterInfo
        : skipNif
          ? ModalIbanAccountStep.EuropeanAccount
          : ModalIbanAccountStep.NifVerification
    },
    CommercialRegisterInfo: {
      current: 10,
      previous: ModalIbanAccountStep.CheckCommercialRegister,
      next: skipNif ? ModalIbanAccountStep.EuropeanAccount : ModalIbanAccountStep.NifVerification
    },
    NifVerification: {
      current: nifVerificationStep,
      previous: ModalIbanAccountStep.AccountUsage,
      next: ModalIbanAccountStep.EuropeanAccount
    },
    EuropeanAccount: {
      current: skipNif ? nifVerificationStep : nifVerificationStep + 1,
      previous: skipNif ? ModalIbanAccountStep.AccountUsage : ModalIbanAccountStep.NifVerification,
      next: ModalIbanAccountStep.ValidatingIban,
      condition_next: ModalIbanAccountStep.VideoVerification
    },

    VideoVerification: {
      current: skipNif ? nifVerificationStep + 1 : nifVerificationStep + 2,
      previous:
        verifiedIban.length > 0
          ? skipNif
            ? ModalIbanAccountStep.AccountUsage
            : ModalIbanAccountStep.NifVerification
          : ModalIbanAccountStep.EuropeanAccount,
      next: ModalIbanAccountStep.Country
    },
    Country: {
      current: skipNif ? nifVerificationStep + 2 : nifVerificationStep + 3,
      previous: ModalIbanAccountStep.VideoVerification,
      next: ModalIbanAccountStep.Loading,
      condition_previous: ModalIbanAccountStep.VideoVerification
    }
  };
  const principalAddressCountry = (() => {
    if (user?.naturalPerson.Address && user?.naturalPerson.Address.length > 0) {
      const arrayAddresss = user?.naturalPerson.Address.filter(
        (address) => address.isPrincipalAddress
      );
      return arrayAddresss[0].Country.toString();
    }
    return null;
  })();

  const [quizResponses, setQuizResponses] = useState<QuizResponses>({
    documentType: null,
    sources_income: [],
    monthly_income: {
      maximum: null,
      minimum: null,
      trans_key: null
    },
    account_reasons: [],
    payments_usage: [],
    european_account: {
      trans_key: null,
      iban: null
    },
    code_promo: null,
    account_usage: {
      trans_key: null
    },
    natural_pro_activity: {
      description: null
    },
    check_register: {
      trans_key: null
    },
    register_info: {
      country: principalAddressCountry,
      number: null
    }
  });

  const handleStep = (step: ModalIbanAccountStep) => {
    switch (step) {
      case ModalIbanAccountStep.NifVerification:
        {
          if (skipNif) {
            setTotalSteps(11);
            if (verifiedIban.length > 0) handleStep(ModalIbanAccountStep.VideoVerification);
            else handleStep(ModalIbanAccountStep.EuropeanAccount);
          } else setStep(ModalIbanAccountStep.NifVerification);
        }
        break;
      case ModalIbanAccountStep.EuropeanAccount:
        {
          if (verifiedIban.length > 0) handleStep(ModalIbanAccountStep.VideoVerification);
          else return setStep(ModalIbanAccountStep.EuropeanAccount);
        }
        break;

      case ModalIbanAccountStep.MonthlyIncome: {
        if (
          !user?.naturalPerson?.minimumMonthlyIncome &&
          !user?.naturalPerson?.maximumMonthlyIncome
        )
          return setStep(ModalIbanAccountStep.MonthlyIncome);
        else {
          return handleStep(ModalIbanAccountStep.AccountReasons);
        }
      }
      case ModalIbanAccountStep.SourcesIncome: {
        if (user?.naturalPerson?.sourcesIncome && user?.naturalPerson?.sourcesIncome.length > 0)
          return handleStep(ModalIbanAccountStep.MonthlyIncome);
        else return setStep(ModalIbanAccountStep.SourcesIncome);
      }

      default:
        setStep(step);
        break;
    }
  };
  const handlePreviousStep = (step: ModalIbanAccountStep) => {
    switch (step) {
      case ModalIbanAccountStep.NifVerification:
        {
          setStep(ModalIbanAccountStep.AccountUsage);
        }
        break;
      case ModalIbanAccountStep.SourcesIncome:
        {
          if (user?.naturalPerson?.sourcesIncome && user?.naturalPerson?.sourcesIncome.length > 0)
            return handlePreviousStep(ModalIbanAccountStep.Start);
          else return setStep(ModalIbanAccountStep.SourcesIncome);
        }
        break;
      case ModalIbanAccountStep.MonthlyIncome: {
        if (
          !user?.naturalPerson?.minimumMonthlyIncome &&
          !user?.naturalPerson?.maximumMonthlyIncome
        )
          return setStep(ModalIbanAccountStep.MonthlyIncome);
        else return handlePreviousStep(ModalIbanAccountStep.SourcesIncome);
      }
      default:
        setStep(step);
        break;
    }
  };

  const handleCodePromoChange = (selectedOption: string) =>
    setQuizResponses((prevState) => ({
      ...(prevState as QuizResponses),
      code_promo: selectedOption
    }));
  const confettiExplosion = () => {
    const duration = 2 * 750;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 100 };

    const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;

    const interval = window.setInterval(() => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 250 * (timeLeft / duration);

      confetti({
        ...defaults,
        particleCount: Math.floor(particleCount),
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
      });
      confetti({
        ...defaults,
        particleCount: Math.floor(particleCount),
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
      });
    }, 150);
  };
  const onCreateBankAccount = async () => {
    if (quizResponses.countryIban !== "FR" && quizResponses.countryIban !== "LU") {
      throw new Error("Error when creating olky account");
    }
    const onSaveQuiz = await saveQuizResponses(quizResponses as QuizResponses);
    if (!onSaveQuiz.success) {
      if (quizResponses.countryIban) throw new Error("Error when creating olky account");
      else throw new Error("Error when saving olky quiz account");
    }
  };

  const handleRequestAccount = async (user: UserType) => {
    setLoadingAuthorized(true);
    const response = await saveRequestAccount(user?.naturalPerson.UUID);
    if (response.success) {
      setLoadingAuthorized(false);
      toast.success(t("olkypass.mainPage.olkypass.modal_authorized_country.toast.success"));
    } else {
      toast.error(t("olkypass.mainPage.olkypass.modal_authorized_country.toast.error"));
    }
    onClose();
    setIbanVerifiedQuiz(undefined);
  };
  // setAttempts(0)
  const checkAuthorizedCountry = (() => {
    if (user?.naturalPerson.Address && user?.naturalPerson.Address.length > 0) {
      const listOfCountry = countries?.countries;
      const arrayAddresss = user?.naturalPerson.Address.filter(
        (address) => address.isPrincipalAddress
      );
      if (listOfCountry) {
        const countryAuthorized = listOfCountry.find(
          (country) => arrayAddresss[0].Country.toString() === country.id.toString()
        );
        if (countryAuthorized && countryAuthorized?.openOlkypayAccount) return true;
        else return false;
      }
    }
    return null;
  })();

  useEffect(() => {
    if (isOpen && checkAuthorizedCountry === false) {
      setStep(ModalIbanAccountStep.WelcomeStep);
      setAttempts(0);
    } else {
      setQuizResponses({
        sources_income: [],
        monthly_income: {
          maximum: null,
          minimum: null,
          trans_key: null
        },
        account_reasons: [],
        payments_usage: [],
        european_account: {
          trans_key: null,
          iban: null
        },
        code_promo: null,
        account_usage: {
          trans_key: null
        },
        natural_pro_activity: {
          description: null
        },
        check_register: {
          trans_key: null
        },
        register_info: {
          country: principalAddressCountry ?? null,
          number: null
        }
      });
    }
  }, [isOpen, checkAuthorizedCountry]);
  useEffect(() => {
    if (step === ModalIbanAccountStep.EuropeanAccount) {
      setAttempts(0);
    }
  }, [step]);
  useEffect(() => {
    if (quizResponses?.account_usage?.trans_key == "perso_pro") {
      setTypeofProAccount(true);
      setNifVerificationStep(inscritRegisterCommerce ? 10 : 9);
    } else {
      setTypeofProAccount(false);
      setNifVerificationStep(7);
    }
    if (quizResponses?.check_register?.trans_key == "yes") {
      setInscritRegisterCommerce(true);
    } else {
      setInscritRegisterCommerce(false);
    }
  }, [quizResponses]);

  useEffect(() => {
    if (step === ModalIbanAccountStep.Loading) {
      setProgress(0);
      timer.current = setInterval(() => {
        elapsed.current += interval;
        setProgress((elapsed.current / totalDuration) * 100);
        if (elapsed.current >= totalDuration) {
          const onLoadingEnd = async () => {
            if (timer.current) clearInterval(timer.current);
            try {
              await onCreateBankAccount();
              await refetchUser();

              setStep(ModalIbanAccountStep.Success);
              confettiExplosion();
            } catch (_) {
              await refetchUser();
              setStep(ModalIbanAccountStep.Error);
            }
          };
          onLoadingEnd();
        }
      }, interval);
    }

    return () => {
      if (timer.current) return clearInterval(timer.current);
    };
  }, [step, attempts]);
  const attemptIbanValidation = useRef<number>(0);
  useEffect(() => {
    if (
      (step === ModalIbanAccountStep.Country &&
        quizResponses?.european_account?.trans_key !== "yes_communicate" &&
        quizResponses?.european_account?.trans_key !== null) ||
      (step === ModalIbanAccountStep.Country && verifiedIban.length === 0 && !ibanVerifiedQuiz)
    ) {
      setQuizResponses((prevState) => ({
        ...(prevState as QuizResponses),
        countryIban: "LU"
      }));
      setStep(ModalIbanAccountStep.Loading);
    }
  }, [step, quizResponses?.european_account?.trans_key, ibanVerifiedQuiz]);
  useEffect(() => {
    if (step !== ModalIbanAccountStep.ValidatingIban) return;
    let timeout: NodeJS.Timeout;
    const timoutTime = 2000;
    const validateIbanFunc = async () => {
      setProgress(((attemptIbanValidation.current + 1) / maxAttempts) * 100);
      try {
        if (!ibanUuid) {
          setStep(ModalIbanAccountStep.EuropeanAccount);
          setProgress(0);
          attemptIbanValidation.current = 0;
          return;
        }
        const response = await isValidatedIban(ibanUuid);
        if (response.data?.valid === false) {
          toast.error(
            t("olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.european_account.error")
          );
          setProgress(100);
          setStep(ModalIbanAccountStep.EuropeanAccount);
          return;
        } else if (response.data?.valid === true) {
          setProgress(100);
          setIbanVerifiedQuiz(quizResponses?.european_account?.iban as string);
          setStep(ModalIbanAccountStep.VideoVerification);

          setProgress(0);
          attemptIbanValidation.current = 0;
          return;
        } else {
          attemptIbanValidation.current++;
          if (attemptIbanValidation.current === maxAttempts - 1) {
            toast.error(
              t("olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.european_account.error")
            );
          }
          if (attemptIbanValidation.current < maxAttempts - 1) {
            timeout = setTimeout(validateIbanFunc, timoutTime);
          } else {
            setStep(ModalIbanAccountStep.EuropeanAccount);
            setProgress(0);
            attemptIbanValidation.current = 0;
          }
        }
      } catch (_e) {
        attemptIbanValidation.current++;
        if (attemptIbanValidation.current === maxAttempts - 1) {
          toast.error(
            t("olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.european_account.error")
          );
        }
        if (attemptIbanValidation.current < maxAttempts - 1) {
          timeout = setTimeout(validateIbanFunc, timoutTime);
        } else {
          setStep(ModalIbanAccountStep.EuropeanAccount);
          setProgress(0);
          attemptIbanValidation.current = 0;
        }
      }
    };
    validateIbanFunc();
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [step]);

  const [ibanUuid, setIbanUuid] = useState<string>();

  const handleIbanUuid = (uuidIban: string) => {
    if (uuidIban) setIbanUuid(uuidIban);
  };
  const handleRefetchUser = async () => {
    await refetchUser();
  };

  return (
    <Modal
      open={isOpen}
      closable={step !== ModalIbanAccountStep.Loading}
      onClose={onClose}
      positionWrapperClassName={"h-full"}
      className="h-full w-full min-w-xl max-w-xl overflow-y-scroll sm:h-max sm:w-full"
      maxWidthClassName={"max-w-3xl"}
    >
      <div className="flex h-auto flex-col justify-center py-6 sm:py-2">
        {step === ModalIbanAccountStep.WelcomeStep &&
          (checkAuthorizedCountry === null ? (
            <>
              <div className="flex flex-row justify-center">
                <div>
                  <div className="flex h-full grow flex-col items-center justify-center">
                    <Logo loading init className="animated mb-8 w-24" />
                  </div>
                </div>
              </div>{" "}
            </>
          ) : !checkAuthorizedCountry ? (
            <>
              <HeaderIbanModal
                bannerCreateAccountUrl={bannerCreateAccountUrl}
                custom_scenario={true}
                id={null}
                status={status}
              ></HeaderIbanModal>
              <>
                <Base className="mb-4 p-4 font-semibold text-base">
                  {t("olkypass.mainPage.olkypass.modal_authorized_country.description")}
                </Base>

                <div className="mt-4 flex flex-row justify-evenly">
                  <Button
                    className="mr-1 w-1/2 w-max shrink-0 bg-white text-primary"
                    theme={Themes.declarative}
                    onClick={() => {
                      onClose();
                    }}
                  >
                    {t("olkypass.mainPage.olkypass.modal_authorized_country.buttons.cancel")}
                  </Button>
                  <Button
                    loading={loadingAuthorized}
                    shortcut="enter"
                    className="ml-1 w-1/2 shrink-0"
                    theme={Themes.primary}
                    onClick={() => {
                      if (user) handleRequestAccount(user);
                    }}
                  >
                    {t("olkypass.mainPage.olkypass.modal_authorized_country.buttons.confirm")}
                  </Button>
                </div>
              </>
            </>
          ) : (
            nothasIban &&
            status === KYCStatusType.DECLARATIVE && (
              <div className="px-6 py-4">
                <WelcomeStep custom_scenario={true} onValidated={() => {}} />
                <div className="mt-auto flex flex-col items-center">
                  <DotsNavigation total={totalSteps} current={0} className="mb-4" />
                  <Button
                    onClick={() => {
                      handleStep(ModalIbanAccountStep.IdentityDocument);
                    }}
                    className="w-auto"
                  >
                    {t("olkypass.general.continue")}
                  </Button>
                </div>
              </div>
            )
          ))}
        {step === ModalIbanAccountStep.IdentityDocument && (
          <div className="px-4 py-4">
            <DocumentTypeStep
              onPrevious={() => setStep(ModalIbanAccountStep.WelcomeStep)}
              custom_scenario={true}
              onValidated={(documentType: KYCDocumentType) => {
                setQuizResponses((state) => ({
                  ...state,
                  documentType: documentType
                }));
                setStep(ModalIbanAccountStep.Start);
              }}
              stepSettings={stepsSettings.IdentityDocument}
              setPrevStep={handlePreviousStep}
              setStep={handleStep}
            />
          </div>
        )}
        {step === ModalIbanAccountStep.Start && (
          <>
            {
              <>
                <HeaderIbanModal
                  bannerCreateAccountUrl={bannerCreateAccountUrl}
                  custom_scenario={true}
                  id={null}
                  status={status}
                ></HeaderIbanModal>

                <>
                  <Base className="mb-4 text-center text-slate-400 dark:text-slate-400">
                    {t(
                      "olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.start.description.part_1"
                    )}
                    <span className="text-sky-500">
                      {" "}
                      {t(
                        "olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.start.description.part_2"
                      )}
                    </span>
                  </Base>
                  <SectionTitleSmall>
                    {t("olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.start.question")}
                  </SectionTitleSmall>

                  <div className="mt-4 mb-4 flex min-w-full flex-col justify-center">
                    <LanguageSelector />
                  </div>
                  <SectionTitleSmall>
                    {t("olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.start.code_promo")}
                  </SectionTitleSmall>

                  <div className="mt-4 mb-4 flex min-w-full flex-col justify-center">
                    <InputLabel
                      autoFocus
                      placeholder={"1234"}
                      noLatinCheck={true}
                      required={false}
                      className={"pb-4"}
                      value={quizResponses?.code_promo as string}
                      onChange={(value: string) => handleCodePromoChange(value)}
                    />
                  </div>
                  <div className="mt-auto mb-4 flex flex-col items-center">
                    <DotsNavigation
                      total={totalSteps}
                      current={stepsSettings.Start.current}
                      className="mb-4"
                    />
                    <div className="flex w-full flex-col-reverse gap-y-2 md:flex-row md:gap-x-2">
                      <Button
                        theme={Themes.white}
                        onClick={() =>
                          handlePreviousStep(stepsSettings.Start.previous as ModalIbanAccountStep)
                        }
                      >
                        {t("olkypass.general.go_back")}
                      </Button>

                      <Button
                        onClick={() => handleStep(stepsSettings.Start.next as ModalIbanAccountStep)}
                      >
                        {t("olkypass.general.continue")}
                      </Button>
                    </div>
                  </div>
                </>
              </>
            }
          </>
        )}
        {step === ModalIbanAccountStep.SourcesIncome && (
          <>
            <HeaderIbanModal
              bannerCreateAccountUrl={bannerCreateAccountUrl}
              custom_scenario={true}
              id={null}
              status={status}
            ></HeaderIbanModal>
            <SourceIncomeStep
              setStep={handleStep}
              quizResponses={quizResponses}
              setQuizResponses={setQuizResponses}
              stepSettings={stepsSettings.SourcesIncome}
              setPrevStep={handlePreviousStep}
              countries={countries}
              totalSteps={totalSteps}
            ></SourceIncomeStep>
          </>
        )}

        {step === ModalIbanAccountStep.NifVerification && (
          <>
            <HeaderIbanModal
              bannerCreateAccountUrl={bannerCreateAccountUrl}
              custom_scenario={true}
              id={null}
              status={status}
            ></HeaderIbanModal>
            <NifVerificationStep
              user={user as UserType}
              stepSettings={stepsSettings.NifVerification}
              setStep={handleStep}
              setPrevStep={handlePreviousStep}
              countries={countries}
              totalSteps={totalSteps}
            />
          </>
        )}
        {step === ModalIbanAccountStep.MonthlyIncome && (
          <>
            <HeaderIbanModal
              bannerCreateAccountUrl={bannerCreateAccountUrl}
              custom_scenario={true}
              id={null}
              status={status}
            ></HeaderIbanModal>
            <MonthlyIncomeStep
              setStep={handleStep}
              quizResponses={quizResponses}
              setQuizResponses={setQuizResponses}
              setPrevStep={handlePreviousStep}
              stepSettings={stepsSettings.MonthlyIncome}
              totalSteps={totalSteps}
            ></MonthlyIncomeStep>
          </>
        )}
        {step === ModalIbanAccountStep.AccountReasons && (
          <>
            <HeaderIbanModal
              bannerCreateAccountUrl={bannerCreateAccountUrl}
              custom_scenario={true}
              id={null}
              status={status}
            ></HeaderIbanModal>
            <AccountReasonStep
              setStep={handleStep}
              quizResponses={quizResponses}
              setQuizResponses={setQuizResponses}
              setPrevStep={handlePreviousStep}
              stepSettings={stepsSettings.AccountReasons}
              totalSteps={totalSteps}
            ></AccountReasonStep>
          </>
        )}
        {step === ModalIbanAccountStep.PaymentUsage && (
          <>
            <HeaderIbanModal
              bannerCreateAccountUrl={bannerCreateAccountUrl}
              custom_scenario={true}
              id={null}
              status={status}
            ></HeaderIbanModal>
            <PaymentUsageStep
              setStep={handleStep}
              quizResponses={quizResponses}
              setQuizResponses={setQuizResponses}
              setPrevStep={handlePreviousStep}
              stepSettings={stepsSettings.PaymentUsage}
              totalSteps={totalSteps}
            ></PaymentUsageStep>
          </>
        )}
        {step === ModalIbanAccountStep.EuropeanAccount && (
          <>
            <HeaderIbanModal
              bannerCreateAccountUrl={bannerCreateAccountUrl}
              custom_scenario={true}
              id={null}
              status={status}
            ></HeaderIbanModal>

            <EuropeanAccountStep
              setStep={handleStep}
              quizResponses={quizResponses}
              setQuizResponses={setQuizResponses}
              setPrevStep={handlePreviousStep}
              stepSettings={stepsSettings.EuropeanAccount}
              totalSteps={totalSteps}
              handleIbanUuid={handleIbanUuid}
              ibanVerified={ibanVerifiedQuiz}
              handleRefetchUser={handleRefetchUser}
              handleSuccess={async () => {
                setIbanVerifiedQuiz(quizResponses?.european_account?.iban as string);
                setStep(ModalIbanAccountStep.VideoVerification);
              }}
            ></EuropeanAccountStep>
          </>
        )}
        {step === ModalIbanAccountStep.ValidatingIban && (
          <div className="flex h-full flex-col items-center justify-center">
            <CardTitle className="mb-4 text-center">
              {t("olkypass.mainPage.olkypass.modalOpenOlkyPay.validating_your_iban")}
            </CardTitle>
            <div className="h-2 w-full rounded-full bg-gray-200">
              <div
                className="h-2 rounded-full"
                style={{
                  width: `${progress}%`,
                  transition: "width 0.1s linear",
                  backgroundColor: "#00afeb"
                }}
              />
            </div>
          </div>
        )}
        {step === ModalIbanAccountStep.VideoVerification && (
          <>
            <HeaderIbanModal
              bannerCreateAccountUrl={bannerCreateAccountUrl}
              custom_scenario={true}
              id={null}
              status={status}
            ></HeaderIbanModal>
            <VideoVerificationStep
              setStep={handleStep}
              custom_scenario={true}
              quizResponses={quizResponses}
              setQuizResponses={setQuizResponses}
              setPrevStep={handlePreviousStep}
              stepSettings={stepsSettings.VideoVerification}
              user={user}
              totalSteps={totalSteps}
            ></VideoVerificationStep>
          </>
        )}
        {step === ModalIbanAccountStep.Country && (
          <>
            <HeaderIbanModal
              bannerCreateAccountUrl={bannerCreateAccountUrl}
              custom_scenario={true}
              id={null}
              status={status}
            ></HeaderIbanModal>
            <>
              <Base className="mb-4">
                {t("olkypass.mainPage.olkypass.modalOpenOlkyPay.choose_country_text")}
              </Base>

              <div className="my-4 mt-auto w-full space-y-4">
                {["FR", "LU"].map((c) =>
                  quizResponses?.european_account?.trans_key !== "yes_communicate" &&
                  quizResponses?.european_account?.trans_key !== null &&
                  c === "FR" ? null : (
                    <RadioCard
                      active={quizResponses?.countryIban === c}
                      key={c}
                      onClick={() =>
                        setQuizResponses((prevState) => ({
                          ...(prevState as QuizResponses),
                          countryIban: c
                        }))
                      }
                    >
                      <Flag countryCode={c} className="mb-4" />
                      <Base>IBAN {c}</Base>
                      <Info>{c}XX XXXX XXXX XXXX XXX</Info>
                    </RadioCard>
                  )
                )}
              </div>

              <div className="mt-auto mb-4 flex flex-col items-center">
                <DotsNavigation
                  total={
                    quizResponses?.account_usage?.trans_key == "perso_pro"
                      ? totalSteps + 3
                      : totalSteps
                  }
                  current={stepsSettings.Country?.current}
                  className="mb-4"
                />
                <div className="flex w-full flex-col-reverse gap-y-2 md:flex-row md:gap-x-2">
                  <Button
                    theme={Themes.white}
                    onClick={() => {
                      if (
                        quizResponses?.european_account?.trans_key === "" ||
                        quizResponses?.european_account?.trans_key === "yes_communicate"
                      ) {
                        setStep(stepsSettings.Country.previous as ModalIbanAccountStep);
                      } else {
                        setStep(stepsSettings.Country.condition_previous as ModalIbanAccountStep);
                      }
                    }}
                  >
                    {t("olkypass.general.go_back")}
                  </Button>

                  <Button
                    disabled={!quizResponses?.countryIban}
                    onClick={() => {
                      setStep(stepsSettings.Country.next as ModalIbanAccountStep);
                    }}
                  >
                    {t("olkypass.mainPage.olkypass.modalOpenOlkyPay.button_create_my_account")}
                  </Button>
                </div>
              </div>
            </>
          </>
        )}
        {step === ModalIbanAccountStep.AccountUsage && (
          <>
            <HeaderIbanModal
              bannerCreateAccountUrl={bannerCreateAccountUrl}
              custom_scenario={true}
              id={null}
              status={status}
            ></HeaderIbanModal>
            <AccountUsageStep
              setStep={handleStep}
              quizResponses={quizResponses}
              setQuizResponses={setQuizResponses}
              stepSettings={stepsSettings.AccountUsage}
              user={user}
              setPrevStep={handlePreviousStep}
              totalSteps={totalSteps}
            ></AccountUsageStep>
          </>
        )}
        {step === ModalIbanAccountStep.NatureProActivity && (
          <>
            <HeaderIbanModal
              bannerCreateAccountUrl={bannerCreateAccountUrl}
              custom_scenario={true}
              id={null}
              status={status}
            ></HeaderIbanModal>
            <NatureProActivityStep
              setStep={handleStep}
              quizResponses={quizResponses}
              setQuizResponses={setQuizResponses}
              stepSettings={stepsSettings.NatureProActivity}
              user={user}
              setPrevStep={handlePreviousStep}
              totalSteps={totalSteps}
            ></NatureProActivityStep>
          </>
        )}
        {step === ModalIbanAccountStep.CheckCommercialRegister && (
          <>
            <HeaderIbanModal
              bannerCreateAccountUrl={bannerCreateAccountUrl}
              custom_scenario={true}
              id={null}
              status={status}
            ></HeaderIbanModal>
            <CheckCommercialRegisterStep
              setStep={handleStep}
              quizResponses={quizResponses}
              setQuizResponses={setQuizResponses}
              stepSettings={stepsSettings.CheckCommercialRegister}
              setPrevStep={handlePreviousStep}
              user={user}
              totalSteps={totalSteps}
            ></CheckCommercialRegisterStep>
          </>
        )}
        {step === ModalIbanAccountStep.CommercialRegisterInfo && (
          <>
            <HeaderIbanModal
              bannerCreateAccountUrl={bannerCreateAccountUrl}
              custom_scenario={true}
              id={null}
              status={status}
            ></HeaderIbanModal>
            <CommercialRegisterInfoStep
              setStep={handleStep}
              quizResponses={quizResponses}
              setQuizResponses={setQuizResponses}
              stepSettings={stepsSettings.CommercialRegisterInfo}
              setPrevStep={handlePreviousStep}
              user={user}
              totalSteps={totalSteps}
            ></CommercialRegisterInfoStep>
          </>
        )}
        {step === ModalIbanAccountStep.Loading && (
          <div className="flex h-full flex-col items-center justify-center">
            <CardTitle className="mb-4 text-center">
              {t("olkypass.mainPage.olkypass.modalOpenOlkyPay.creating_your_account")}
            </CardTitle>
            <div className="h-2 w-full rounded-full bg-gray-200">
              <div
                className="h-2 rounded-full bg-primary"
                style={{
                  width: `${progress}%`,
                  transition: "width 0.1s linear",
                  backgroundColor: "#00afeb"
                }}
              />
            </div>
          </div>
        )}
        {step === ModalIbanAccountStep.Success && (
          <>
            <div className="flex flex-col justify-between text-center">
              <CardTitle>
                {" "}
                {t("olkypass.mainPage.olkypass.modalOpenOlkyPay.account_open_creation_title")}
              </CardTitle>
              <Base className="px-6 py-4 text-primary">
                {t("olkypass.mainPage.olkypass.modalOpenOlkyPay.account_open_creation_title2")}
              </Base>
            </div>

            <div className="mt-7 mb-6 flex h-2/5 w-full items-center justify-center overflow-hidden rounded-sm sm:mt-5 md:h-1/2">
              <div className="relative flex h-full w-full flex-col justify-between overflow-hidden rounded-sm border-2 border-slate-50 p-8 text-center">
                <div className="absolute right-0 bottom-0 opacity-20">
                  <Image src="/images/olkypass/quiz-success-bg.png" alt="leaves" />
                </div>
                <Base className="">
                  {" "}
                  {t("olkypass.mainPage.olkypass.modalOpenOlkyPay.account_open_creation_title3")}
                </Base>
                <Base className="mt-4">
                  {t(
                    "olkypass.mainPage.olkypass.modalOpenOlkyPay.account_open_creation_description"
                  )}
                </Base>
                <Base className="mt-4">
                  {" "}
                  {t(
                    "olkypass.mainPage.olkypass.modalOpenOlkyPay.account_open_creation_description2"
                  )}
                </Base>
                {/* IBAN and BIC Container */}
              </div>
            </div>
            <>
              <div className="mt-auto flex flex-col items-center">
                <DotsNavigation total={3} current={2} className="mt-6 mb-4" />

                <div className="flex w-60 flex-col-reverse gap-y-2 md:flex-row md:gap-x-2">
                  <Button
                    theme={Themes.white}
                    onClick={() => {
                      onClose();
                    }}
                  >
                    {t("olkypass.general.close")}
                  </Button>
                </div>
              </div>
            </>
          </>
        )}
        {step === ModalIbanAccountStep.Error && (
          <div className="p-2">
            <>
              <CardTitle className="mb-4 text-center">
                {t("olkypass.mainPage.olkypass.modalOpenOlkyPay.error")}
              </CardTitle>
              <Base className="mb-4 text-center text-slate-400 dark:text-slate-400">
                {t("olkypass.mainPage.olkypass.modalOpenOlkyPay.error_description")}
              </Base>
              <div className="mt-auto flex flex-col items-center">
                {/*<DotsNavigation total={3} current={2} className="mb-4" />*/}
                <Button
                  onClick={() => {
                    onClose();
                  }}
                >
                  {t("olkypass.accountPage.dialog.newItems.button.finish")}
                </Button>
              </div>
            </>
          </div>
        )}
      </div>
    </Modal>
  );
};
